/**
 * MONATSPAUSCHALE — reine Ableitungen, kein Schreibpfad.
 *
 * Das Projekt zahlt einen festen Monatsbetrag (z. B. 1.200 EUR fuer die
 * Begehungen eines Monats, egal ob 4 oder 5 stattfanden). Die Pauschale ist
 * eine EIGENE LV-Position (Einheit Monat, assignment 'flatrate'), und ihre
 * Deckung steht als `flatRate: FlatRateCoverage` AUF dieser Position.
 *
 * KERNREGEL, ueberall dieselbe: eine GEDECKTE Position laesst `quantityUsed`
 * weiterlaufen, ihr GELDSTAND steht still. Dieses Modul entscheidet nur,
 * WELCHE Positionen gedeckt sind und WELCHE Monate der Pauschale noch offen
 * sind — die Geldwirkung ziehen openQuantity, computeCumulativePositions und
 * getProjectFinancials aus genau diesen Antworten. `quantityUsed` fasst hier
 * nichts an: Begehungszaehler, Begehungsrhythmus, Projektcheck und
 * "versendet ohne Buchung" duerfen sich durch eine Pauschale nicht aendern.
 *
 * WARUM EIGENES BLATT-MODUL (wie serviceUnits): die Deckung wird von beiden
 * Seiten gebraucht — vom Rechnungsstrang (invoiceLogic/projectLv) und von den
 * Dialogen. Ein Import in Richtung projectLv oder Supabase wuerde frueher
 * oder spaeter denselben Importzyklus bauen, der isHourlyUnit schon einmal
 * aus projectLv vertrieben hat. Dieses Modul importiert deshalb nur Typen.
 *
 * NIE still automatisch buchen (Betreiber-Entscheidung 4): dieses Modul
 * schlaegt nur vor. Jeder stille Zaehlerschreiber in diesem Repo hat schon
 * einmal Geld verbogen.
 */

import type { FlatRateCoverage, ServiceTransaction } from '../types';
// serviceLogUtils ist ein Blattmodul mit ausschliesslich Typ-Importen — der
// Import haelt dieses Modul blattnah (kein Zyklus).
import { FLAT_PROOF_PREFIX, isFlatProofEntry } from './serviceLogUtils';

/** Das Wenige einer Position, aus dem sich die Deckung ergibt. */
export interface FlatCoverageServiceLike {
    id: string;
    assignment?: string;
    lvPosition?: string;
    title?: string;
    flatRate?: FlatRateCoverage;
    /**
     * Mengenstand der Position — optional, weil die Deckungsaufloesung ihn
     * nicht braucht. Die Rechnungspruefung (flatRateIssues, Regel 4) liest
     * ihn, um "gebuchter Monat, aber der Beleg fordert nichts" zu erkennen:
     * offenes Geld der Pauschale ist quantityUsed - quantityBilled. Beide
     * Aufrufer reichen ohnehin volle ServiceItem-Listen durch.
     */
    quantityUsed?: number;
    quantityBilled?: number;
}

/** Das Wenige eines Protokolleintrags, aus dem sich der Monatsstand ergibt. */
export interface FlatLogEntryLike {
    serviceId?: string;
    bookingReference?: string;
}

/**
 * Traegt diese Position eine Monatspauschale?
 *
 * Massgeblich ist das Deckungsobjekt `flatRate`, nicht das assignment: das
 * assignment 'flatrate' ist Anzeige und Sortierung, die Deckung ist Geld.
 * Ein flatRate-Objekt ohne brauchbaren scope zaehlt nicht — eine halb
 * angelegte Pauschale darf keine Positionen stilllegen.
 */
export const isFlatRateService = (s: FlatCoverageServiceLike | null | undefined): boolean => {
    const scope = s?.flatRate?.scope;
    return scope === 'inspection' || scope === 'all' || scope === 'explicit';
};

/**
 * Ist diese Position fuer den scope 'inspection' eine Begehungsposition?
 *
 * Die WEITE Begehungsdefinition (assignment, ganze Gruppen 2.7./2.8., Titel)
 * — dieselbe Breite wie countsTowardInspectionCounter in projectLv, aber
 * OHNE dessen Stundenausnahme, und der Unterschied ist Absicht: die
 * Stundenausnahme schuetzt den BegehungsZAEHLER davor, Stunden als Stueck zu
 * zaehlen. Hier geht es um GELD: eine Begehungsposition nach Zeitaufwand
 * (2.7.40, Einheit Stunden) ist genauso eine Begehung und muss von einer
 * Begehungspauschale genauso gedeckt sein — sonst kostete jede Stunde weiter
 * extra und die Pauschale waere fuer Stundenprojekte wirkungslos.
 *
 * Lokale Kopie statt Import aus projectLv: siehe Modulkommentar (Blatt-Modul).
 */
const isInspectionCoverageTarget = (s: FlatCoverageServiceLike): boolean =>
    s.assignment === 'inspection' ||
    !!(s.lvPosition && (s.lvPosition.startsWith('2.7.') || s.lvPosition.startsWith('2.8.'))) ||
    (s.title || '').toLowerCase().includes('begehung');

/**
 * Loest die Deckung aller Pauschalpositionen gegen die Positionsliste auf:
 * gedeckte Positions-Id -> Id der deckenden Pauschalposition.
 *
 * Regeln, alle mit Grund:
 *  - Eine Pauschale deckt NIE sich selbst und NIE eine andere Pauschale.
 *    Deckte sie sich selbst, stuende ihr eigener Geldstand still und der
 *    Monatsbetrag waere nicht mehr forderbar; deckte sie eine andere
 *    Pauschale, legte eine 'all'-Pauschale die zweite still — zwei parallele
 *    Pauschalen (etwa Begehungen und Dokumentation getrennt) muessen beide
 *    forderbar bleiben.
 *  - 'explicit' deckt nur Ids, die es in der Liste GIBT. Eine geloeschte oder
 *    umgezogene Position darf keinen Phantomeintrag in der Deckung lassen.
 *  - Decken mehrere Pauschalen dieselbe Position, gewinnt die ERSTE in
 *    LV-Reihenfolge — deterministisch statt zufaellig, damit Anzeige und
 *    Rechnung dieselbe Zuordnung sehen. Eine doppelt gedeckte Position ist
 *    ohnehin ein Anlagefehler, den die UI melden soll, keine Rechenfrage.
 */
export const resolveFlatCoverage = (
    services: FlatCoverageServiceLike[] | null | undefined,
): Map<string, string> => {
    const coverage = new Map<string, string>();
    const list = Array.isArray(services) ? services : [];
    const flats = list.filter(isFlatRateService);
    if (flats.length === 0) return coverage;
    const flatIds = new Set(flats.map((f) => f.id));

    flats.forEach((flat) => {
        const rate = flat.flatRate as FlatRateCoverage;
        const explicitIds = rate.scope === 'explicit' ? new Set(rate.serviceIds || []) : null;
        list.forEach((s) => {
            if (!s?.id || flatIds.has(s.id)) return;
            if (coverage.has(s.id)) return;
            const covered =
                rate.scope === 'all' ||
                (rate.scope === 'inspection' && isInspectionCoverageTarget(s)) ||
                (explicitIds !== null && explicitIds.has(s.id));
            if (covered) coverage.set(s.id, flat.id);
        });
    });
    return coverage;
};

/**
 * Ist diese Position durch eine Monatspauschale gedeckt?
 *
 * Bequemlichkeitsform von resolveFlatCoverage fuer Einzelabfragen. Wer viele
 * Positionen prueft, holt sich die Map einmal selbst.
 */
export const isCovered = (
    services: FlatCoverageServiceLike[] | null | undefined,
    serviceId: string,
): boolean => resolveFlatCoverage(services).has(serviceId);

/**
 * Der ruhige Hinweis der Ansichten: gedeckte Positions-Id -> "in der
 * Monatspauschale enthalten (LV-Nr. Titel der Pauschale)".
 *
 * EINE Formulierung fuer alle Listen (Projekt-Detailansicht, Buchhaltung,
 * offene Positionen), wortgleich mit dem Zusatz auf dem Leistungsnachweis
 * (generatorPerformance) — zwei Wortlaute fuer denselben Sachverhalt laesen
 * sich wie zwei verschiedene Zustaende. Der Klammerzusatz benennt, WELCHE
 * Pauschale deckt: bei zwei parallelen Pauschalen (Begehungen und
 * Dokumentation getrennt) ist "enthalten" ohne den Namen keine Antwort.
 *
 * Rechnet IMMER frisch ueber resolveFlatCoverage statt den Stempel
 * flatCoveredBy zu lesen: der Stempel existiert nur auf Listen aus
 * getNormalizedServices, die Projektansicht arbeitet aber auf dem rohen
 * globalData.services-Bestand. Beide Wege ergeben dieselbe Zuordnung
 * (dieselbe Gewinner-Regel), und so gibt es keinen Listen-Typ, auf dem der
 * Hinweis stillschweigend fehlt.
 */
export const flatCoverageHints = (
    services: FlatCoverageServiceLike[] | null | undefined,
): Map<string, string> => {
    const hints = new Map<string, string>();
    const list = Array.isArray(services) ? services : [];
    if (list.length === 0) return hints;
    const byId = new Map(list.map((s) => [s.id, s]));
    resolveFlatCoverage(list).forEach((flatId, coveredId) => {
        const flat = byId.get(flatId);
        const name = [flat?.lvPosition, flat?.title]
            .filter((part) => !!(part || '').trim())
            .join(' ')
            .trim();
        hints.set(
            coveredId,
            name
                ? `in der Monatspauschale enthalten (${name})`
                : 'in der Monatspauschale enthalten',
        );
    });
    return hints;
};

/**
 * Nur eine ISO-artige Datumsangabe mit vierstelligem Jahr und gueltigem
 * Monat wird gelesen. KEIN Date-Parsing: `new Date('2026-08-01')` liegt in
 * westlichen Zeitzonen am Vorabend des 31.07. — ein ueber Mitternacht
 * verrutschter Monat waere hier ein falscher Buchungsschluessel.
 *
 * Der Praefix-Match ist Absicht ('2026-08-29' und '2026-08-29T10:15' sind
 * regulaere Eingaben), aber hinter dem Monat muss das Datum enden oder mit
 * '-' weitergehen — Lookahead statt Endanker, damit '2026-08xyz' zurueck-
 * gewiesen wird, ohne die vollen ISO-Daten auszusperren.
 */
const MONTH_SHAPE = /^(\d{4})-(0[1-9]|1[0-2])(?=$|-)/;

/** Jahr/Monat als fortlaufender Monatsindex — Arithmetik ohne Date-Objekt. */
const parseMonthIndex = (dateIso?: string | null): number | null => {
    const m = MONTH_SHAPE.exec(String(dateIso ?? '').trim());
    if (!m) return null;
    return parseInt(m[1], 10) * 12 + (parseInt(m[2], 10) - 1);
};

const keyForMonthIndex = (index: number): string => {
    const year = Math.floor(index / 12);
    const month = index - year * 12 + 1;
    return `FLAT-${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`;
};

/** Die Form einer gueltigen Monatsbuchungs-Referenz. */
const FLAT_KEY_SHAPE = /^FLAT-\d{4}-(0[1-9]|1[0-2])$/;

/**
 * Buchungsschluessel des Monats: 'FLAT-YYYY-MM'.
 *
 * Der Schluessel ist der RIEGEL gegen Doppelbuchung, nicht die Menge
 * (Betreiber-Entscheidung 1): ein angebrochener Monat darf mit 0,5 gebucht
 * werden und bleibt trotzdem derselbe Monat — ein zweiter Eintrag mit
 * demselben Schluessel ist immer ein Fehler. null fuer alles Unlesbare,
 * damit aus einem kaputten Datum nie ein Schluessel wie 'FLAT-NaN-NaN'
 * entsteht, der den Riegel unterlaufen wuerde.
 */
export const flatMonthKey = (dateIso?: string | null): string | null => {
    const index = parseMonthIndex(dateIso);
    return index === null ? null : keyForMonthIndex(index);
};

/**
 * Beschreibungstext der Monatsbuchung: 'Monatspauschale MM/YYYY'.
 *
 * Fester Wortlaut, damit die Eintraege im Protokoll, auf dem
 * Leistungsnachweis und in jeder spaeteren Pruefung als Monatsbuchung
 * erkennbar sind — eine Buchung ohne sprechende description hat hier schon
 * einmal jede Pruefung uebersehen (Nachbuchungs-Regel im Archivabgleich).
 */
export const flatMonthLabel = (dateIso?: string | null): string | null => {
    const m = MONTH_SHAPE.exec(String(dateIso ?? '').trim());
    return m ? `Monatspauschale ${m[2]}/${m[1]}` : null;
};

/**
 * Welche Monate sind auf der Pauschalposition schon gebucht?
 *
 * Massgeblich ist NUR die bookingReference in der Form 'FLAT-YYYY-MM' — nie
 * das Datumsfeld des Eintrags (ein Folgebericht erbt Datumsfelder, die
 * Referenz wird beim Buchen gesetzt) und nie die Menge (0,5 fuer einen
 * angebrochenen Monat verbraucht den Monat trotzdem). Fremde Eintraege auf
 * derselben Position (BU-…, ALT-…, freie Nachtraege) zaehlen nicht als
 * Monat.
 */
export const bookedFlatMonths = (
    serviceLog: FlatLogEntryLike[] | null | undefined,
    flatServiceId: string,
): Set<string> => {
    const booked = new Set<string>();
    (Array.isArray(serviceLog) ? serviceLog : []).forEach((entry) => {
        if (!entry || entry.serviceId !== flatServiceId) return;
        const ref = String(entry.bookingReference ?? '').trim();
        if (FLAT_KEY_SHAPE.test(ref)) booked.add(ref);
    });
    return booked;
};

export interface OpenFlatMonthsArgs {
    /** Projektbeginn (ISO). Ohne lesbaren Beginn gibt es keine Vorschlaege. */
    start?: string | null;
    /** Projektende (ISO); fehlt es, laeuft die Pauschale offen weiter. */
    end?: string | null;
    /** Heutiges Datum (ISO) — Parameter statt new Date(), damit testbar. */
    today?: string | null;
    serviceLog?: FlatLogEntryLike[] | null;
    flatServiceId: string;
}

/**
 * Welche Monate der Laufzeit sind noch nicht gebucht? Ergebnis sind die
 * Buchungsschluessel ('FLAT-YYYY-MM') in zeitlicher Reihenfolge.
 *
 * Der Vorschlag haengt am KALENDER und an der Projektlaufzeit, NICHT am
 * Begehungsprotokoll (Betreiber-Entscheidung 2): ein Monat ganz ohne
 * Begehung wird trotzdem gefordert — die Pauschale kauft Bereitschaft, nicht
 * Stueckzahl. Deshalb nimmt diese Funktion das Protokoll der GEDECKTEN
 * Positionen gar nicht erst entgegen; serviceLog dient nur dem Abzug der
 * bereits gebuchten Monate der Pauschale selbst.
 *
 * Der LAUFENDE Monat wird nie vorgeschlagen: erst ein abgeschlossener Monat
 * ist eine Forderung. Der Endmonat des Projekts wird dagegen vorgeschlagen,
 * sobald das Projektende erreicht ist, auch wenn das Projekt mitten im
 * laufenden Monat endete — angebrochene Monate schlagen als 1 auf und die
 * Menge bleibt frei ueberschreibbar (Betreiber-Entscheidung 1).
 *
 * Unlesbarer Beginn oder unlesbares Heute -> keine Vorschlaege. Ein
 * AUSGEFUELLTES, aber unlesbares Ende ebenso: es als "offen" zu lesen
 * schluege Monate ueber das gewollte Projektende hinaus vor. Lieber kein
 * Vorschlag als ein Vorschlag aus geratenen Monaten.
 */
export const openFlatMonths = ({
    start,
    end,
    today,
    serviceLog,
    flatServiceId,
}: OpenFlatMonthsArgs): string[] => {
    const startIndex = parseMonthIndex(start);
    const todayIndex = parseMonthIndex(today);
    if (startIndex === null || todayIndex === null) return [];
    // Der letzte abgeschlossene Monat ist der Monat VOR dem heutigen.
    let lastIndex = todayIndex - 1;
    const endIndex = parseMonthIndex(end);
    if (endIndex === null && String(end ?? '').trim() !== '') return [];
    if (endIndex !== null && endIndex < lastIndex) lastIndex = endIndex;
    // Der Endmonat ist faellig, SOBALD das Projektende erreicht ist — auch
    // wenn es im laufenden Monat liegt. Ohne diese Zeile boete der Vorschlag
    // den Endmonat erst ab dem Folgemonat an, waehrend die Rechnungspruefung
    // (flat_month_unbooked) ihn fuer die Schlussrechnung schon verlangt: der
    // Blocker verwiese auf einen Vorschlag, der den Monat noch gar nicht
    // hergibt. Tagesgenauer Vergleich der ISO-Datumsteile statt Date-Parsing
    // (dieselbe Zeitzonen-Vorsicht wie bei MONTH_SHAPE).
    if (
        endIndex !== null &&
        String(end ?? '').trim().slice(0, 10) <= String(today ?? '').trim().slice(0, 10)
    ) {
        lastIndex = endIndex;
    }
    if (lastIndex < startIndex) return [];

    const booked = bookedFlatMonths(serviceLog, flatServiceId);
    const open: string[] = [];
    for (let index = startIndex; index <= lastIndex; index++) {
        const key = keyForMonthIndex(index);
        if (!booked.has(key)) open.push(key);
    }
    return open;
};

// ════════════════════════════════════════════════════════════════════════════
// NACHWEISZEILEN — auf die Pauschale buchen, ohne den Monat zu veraendern
// ════════════════════════════════════════════════════════════════════════════
//
// Betreiber 30.09.2026: „wenn Pauschale, dann soll es moeglich sein, darauf
// immer zu buchen; Eintraege gehen in den Leistungsnachweis, aber der Zaehler
// veraendert sich nicht fuer den Monat". Und: der Versand eines
// Begehungsberichts bucht in einem Pauschalprojekt auf die Pauschale, die
// Begehungsposition bekommt nichts mehr.
//
// Die Zeile traegt Menge 0 und die Referenz NW-… (serviceLogUtils,
// FLAT_PROOF_PREFIX). quantityUsed der Pauschale bleibt, wie er ist — ihr
// Geld zaehlt weiter allein ueber FLAT-JJJJ-MM. Vorher hob jede Buchung auf
// die Pauschale (Schnellbuchung, Nachbuchen, Nachweis-Editor) quantityUsed an
// und machte damit einen Monat Geld ohne Monatsschluessel.

/**
 * Die Pauschale, auf die Begehungen als Nachweis gebucht werden — oder null.
 *
 * In LV-Reihenfolge die erste Pauschale mit scope 'inspection' oder 'all';
 * sonst die erste 'explicit'-Pauschale, deren Liste eine Begehungsposition
 * enthaelt. Ein Projekt ohne Begehungsposition (reines Pauschal-LV) findet
 * seine Pauschale ueber die erste Regel — genau dort gab es bisher gar kein
 * Buchungsziel, und der Versand meldete „keine Position".
 */
export const inspectionFlatTarget = <T extends FlatCoverageServiceLike>(
    services: T[] | null | undefined,
): T | null => {
    const list = Array.isArray(services) ? services : [];
    const flats = list.filter(isFlatRateService);
    const direct = flats.find((f) => f.flatRate?.scope === 'inspection' || f.flatRate?.scope === 'all');
    if (direct) return direct;
    return (
        flats.find((f) => {
            if (f.flatRate?.scope !== 'explicit') return false;
            const ids = new Set(f.flatRate.serviceIds || []);
            return list.some((s) => s?.id && ids.has(s.id) && !isFlatRateService(s) && isInspectionCoverageTarget(s));
        }) ?? null
    );
};

/**
 * Referenz einer Nachweiszeile: 'NW-' + Berichtsschluessel, sonst 'NW-' + id.
 *
 * Aus dem Berichtsschluessel abgeleitet, damit derselbe Bericht auf derselben
 * Pauschale immer dieselbe Referenz traegt — ein zweiter Versand faellt dann
 * schon am Ref-Riegel (isAlreadyBooked) heraus und nicht erst am Text.
 */
export const flatProofReference = (
    reportRef: string | null | undefined,
    entryId: string,
    opts?: { begehung?: boolean },
): string => {
    const ref = String(reportRef ?? '').trim();
    if (ref) return `${FLAT_PROOF_PREFIX}${ref}`;
    return opts?.begehung ? `${FLAT_PROOF_BEGEHUNG_PREFIX}${entryId}` : `${FLAT_PROOF_PREFIX}${entryId}`;
};

/**
 * Referenz-Praefix einer HAND-BEGEHUNG ohne Bericht auf der Pauschale
 * ('NW-BEG-…', faengt ebenfalls mit 'NW-' an).
 *
 * Wer im Pauschalprojekt eine Begehung von Hand bucht (Schnellbuchung auf
 * 2.7.10, umgelenkt auf die Pauschale), bucht eine BEGEHUNG — auch unter einer
 * 'all'- oder 'explicit'-Pauschale, wo eine Handbuchung sonst genauso eine
 * Baubesprechung sein koennte. Ohne diese Kennung zaehlte sie dort nirgends
 * als Begehung (Rhythmus, Kachel, Cockpit), obwohl sie vorher als +1 auf
 * 2.7.10 eine war (Opus-Review 30.09.). Die Kennung reist in der Referenz und
 * damit im Journal mit.
 */
export const FLAT_PROOF_BEGEHUNG_PREFIX = 'NW-BEG-';

/** Der eine Hinweis an jeder Stelle, an der auf die Pauschale gebucht wird. */
export const FLAT_PROOF_HINT =
    'Nachweis in der Monatspauschale – der Monat ändert sich nicht, abgerechnet wird nur der Monat.';

/** Was eine Nachweiszeile braucht — alles vom Aufrufer, dieses Modul erfindet nichts. */
export interface FlatProofEntryInput {
    /** Id der neuen Protokollzeile (Aufrufer: crypto.randomUUID()). */
    id: string;
    flatServiceId: string;
    /** Leistungsdatum (ISO). */
    date: string;
    /** Buchungsvorgang (ISO). */
    bookedAt: string;
    performerId: string;
    performerName: string;
    description: string;
    reportRef?: string | null;
    isSubstitution?: boolean;
    /** Hand-Begehung ohne Bericht (Referenz 'NW-BEG-…'); mit reportRef wirkungslos. */
    begehung?: boolean;
}

/**
 * Die Nachweiszeile auf der Pauschale: Menge 0, Referenz NW-…, kein Zaehler.
 *
 * Rein: liefert nur die Zeile. Der Aufrufer haengt sie ans Protokoll und
 * fasst quantityUsed/status der Pauschale NICHT an.
 */
export const planFlatProofEntry = (input: FlatProofEntryInput): ServiceTransaction => ({
    id: input.id,
    date: input.date,
    bookedAt: input.bookedAt,
    serviceId: input.flatServiceId,
    quantity: 0,
    performerId: input.performerId,
    performerName: input.performerName,
    isSubstitution: input.isSubstitution ?? false,
    description: input.description,
    ...(input.reportRef ? { reportRef: input.reportRef } : {}),
    bookingReference: flatProofReference(input.reportRef, input.id, { begehung: input.begehung }),
});

/**
 * Ist eine Buchung auf DIESE Position eine Nachweiszeile statt einer Menge?
 *
 * Eine Stelle fuer alle Buchungswege (Versand, Nachbuchen, Nachweis-Editor,
 * Schnellbuchung): jede Pauschale nimmt nur noch Nachweise an. Monate bucht
 * allein planFlatMonthBooking (FLAT-JJJJ-MM).
 */
export const bookingIsFlatProof = (service: FlatCoverageServiceLike | null | undefined): boolean =>
    isFlatRateService(service);

type ProofEntryLike = { bookingReference?: string | null; reportRef?: string | null; description?: string | null } | null | undefined;

/** Nachweiszeile, aber keine Zusatzleistung (die traegt denselben Ref, ist aber keine Begehung). */
const isMainProof = (entry: ProofEntryLike): boolean =>
    isFlatProofEntry(entry) && !String(entry?.description ?? '').startsWith('Zusatzleistung');

/**
 * Belegt diese Nachweiszeile die Begehung EINES BERICHTS — eigener Bericht
 * ('RPT-<abschnitt>-N', Text „Bericht #N abgeschlossen") ODER Tochterbericht
 * ('RPT-CHILD-…' im Protokoll der Mutter)?
 *
 * Das ist die Journal-Aussage `coversInspection`: dieselbe Antwort wie die
 * Ableitung (serviceJournal.inferInspectionCoverage, SQL Nr. 134). Eine
 * Hand-Begehung ohne Bericht belegt keinen Bericht (Codex-Befund 30.09.).
 */
export const isReportProofEntry = (entry: ProofEntryLike): boolean =>
    isMainProof(entry)
    && (String(entry?.reportRef ?? '').startsWith('RPT-') || /^Bericht #\d+ abgeschlossen$/.test(String(entry?.description ?? '')));

/**
 * Belegt diese Nachweiszeile einen EIGENEN Bericht dieses Projekts — also
 * eine Berichtsnummer, die der Zaehler (current_inspection_number) vergeben
 * hat? Tochterberichte (RPT-CHILD-…) zaehlen in IHREM Projekt, Hand-Begehungen
 * verbrauchen keine Nummer.
 *
 * Fuer alles, was den Berichtszaehler gegen das LV haelt (Nachtragsdialog,
 * Projektcheck): mit Hand- oder Tochternachweisen zoege der Abgleich 2.7.10
 * unter die echten Buchungen und schriebe eine negative Korrektur
 * (Opus-Review 30.09.).
 */
export const isOwnReportProofEntry = (entry: ProofEntryLike): boolean => {
    if (!isReportProofEntry(entry)) return false;
    const ref = String(entry?.reportRef ?? '');
    if (ref.startsWith('RPT-CHILD-')) return false;
    return ref.startsWith('RPT-') || /^Bericht #\d+ abgeschlossen$/.test(String(entry?.description ?? ''));
};

/**
 * Zaehlt diese Nachweiszeile als BEGEHUNG (Anzeige: Kacheln, Cockpit,
 * Rhythmus, Liegezeit)?
 *
 * Mit Berichtsbezug immer (eigener oder Tochterbericht). Eine Hand-Begehung
 * ('NW-BEG-…') immer. Sonst ohne Bericht nur auf einer reinen
 * Begehungspauschale (scope 'inspection') — auf einer 'all'-Pauschale kann
 * eine Handbuchung auch eine Baubesprechung sein.
 */
export const isInspectionProofEntry = (
    entry: ProofEntryLike,
    flat: FlatCoverageServiceLike | null | undefined,
): boolean => {
    if (!isMainProof(entry)) return false;
    if (isReportProofEntry(entry)) return true;
    if (String(entry?.bookingReference ?? '').startsWith(FLAT_PROOF_BEGEHUNG_PREFIX)) return true;
    return flat?.flatRate?.scope === 'inspection';
};

/**
 * Ist diese Referenz eine Monatsbuchung ('FLAT-YYYY-MM')?
 *
 * Exportiert, weil die Rechnungspruefung (invoiceLogic) Monatsbuchungen
 * ZAEHLEN muss: bookedFlatMonths liefert eine Menge und verschluckt damit
 * genau den Fall, um den es dort geht — denselben Monat zweimal im Protokoll.
 */
export const isFlatBookingReference = (ref?: string | null): boolean =>
    FLAT_KEY_SHAPE.test(String(ref ?? '').trim());

/**
 * Anzeigetext eines Buchungsschluessels: 'FLAT-2026-05' -> '05/2026'.
 *
 * Fuer Pruefmeldungen, die einen Monat benennen muessen. null fuer alles,
 * was kein gueltiger Schluessel ist — eine Meldung mit 'undefined/NaN'
 * waere schlimmer als gar keine.
 */
export const flatKeyLabel = (key?: string | null): string | null => {
    const raw = String(key ?? '').trim();
    if (!FLAT_KEY_SHAPE.test(raw)) return null;
    return `${raw.slice(10, 12)}/${raw.slice(5, 9)}`;
};

/**
 * LEISTUNGSDATUM eines Pauschalmonats: der letzte Tag des Monats, 12:00 UTC
 * ('2026-07-31T12:00:00.000Z' fuer 'FLAT-2026-07').
 *
 * Die Monatsbuchung ist erbrachte Leistung (sie erhoeht quantityUsed), und
 * das Datumsfeld eines Protokolleintrags ist das Datum der LEISTUNG — der
 * Vorgang steht in bookedAt. Trug der Eintrag das Buchungsdatum, lag ein am
 * 07.09. gebuchter Juli in der Rechnungsansicht im September: Zeitraumfilter
 * (loggedQuantityInPeriod), Monatsuebersicht (openByMonth), Rechnungslauf
 * (quantityInPeriod) und Leistungsnachweis ordnen alle nach diesem Feld. Ein
 * Zeitraum Juli–August fand die Pauschale dann nicht (flat_month_unbilled
 * sperrte), ein Zeitraum bis heute verlangte den ungebuchten laufenden Monat
 * (flat_month_unbooked sperrte) — ein nach Monatsende gebuchter Monat war so
 * nie abrechenbar (5633, 07.09.2026).
 *
 * Monatsende statt -anfang: die Pauschale kauft Bereitschaft fuer den ganzen
 * Monat, erbracht ist sie mit dessen Ablauf — ein Stichtag mitten im Monat
 * zaehlt ihn deshalb nicht mit. 12:00 UTC, damit der Ortstag (Europe/Berlin,
 * service_journal_local_day) in jeder Zeitzone derselbe Kalendertag bleibt.
 * null fuer alles, was kein gueltiger Schluessel ist.
 */
export const flatKeyServiceDate = (key?: string | null): string | null => {
    const raw = String(key ?? '').trim();
    if (!FLAT_KEY_SHAPE.test(raw)) return null;
    const year = parseInt(raw.slice(5, 9), 10);
    const month = parseInt(raw.slice(10, 12), 10);
    // Tag 0 des Folgemonats = letzter Tag dieses Monats (UTC, kein Ortszeit-Sprung).
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return `${raw.slice(5, 9)}-${raw.slice(10, 12)}-${String(lastDay).padStart(2, '0')}T12:00:00.000Z`;
};

/**
 * Alle Monatsschluessel zwischen zwei Tagesdaten (einschliesslich), in
 * zeitlicher Reihenfolge.
 *
 * Fuer die Rechnungspruefung: welcher Monat liegt im Abrechnungszeitraum?
 * Unlesbare Grenzen -> leere Liste, aus demselben Grund wie bei
 * openFlatMonths: lieber keine Aussage als eine aus geratenen Monaten.
 */
export const flatMonthKeysInRange = (
    fromIso?: string | null,
    toIso?: string | null,
): string[] => {
    const fromIndex = parseMonthIndex(fromIso);
    const toIndex = parseMonthIndex(toIso);
    if (fromIndex === null || toIndex === null || toIndex < fromIndex) return [];
    const keys: string[] = [];
    for (let index = fromIndex; index <= toIndex; index++) keys.push(keyForMonthIndex(index));
    return keys;
};

// ═══════════════════════════════════════════════════════════════════════════
// Nachtrag: "in der Monatspauschale enthalten: ja / nein"
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die ausdrueckliche Entscheidung des Bearbeiters fuer EINE neue Position.
 * Es gibt bewusst keinen dritten Wert und keine Vorbelegung
 * (Betreiber-Entscheidung 3): "keine Entscheidung" ist der Zustand, der das
 * Anlegen sperrt, kein Wert, den man speichern koennte.
 */
export type FlatInclusionDecision = 'included' | 'excluded';

/** Zaehlt auch eine halb angelegte Pauschale (assignment ohne Deckungsobjekt). */
const isFlatRateLike = (s: FlatCoverageServiceLike): boolean =>
    isFlatRateService(s) || s.assignment === 'flatrate';

/**
 * Welche NEUEN Positionen brauchen noch die Entscheidung "in der
 * Monatspauschale enthalten: ja/nein"?
 *
 * Die Regel hinter dem Nachtragsdialog (Betreiber-Entscheidung 3): in einem
 * Projekt MIT Pauschalposition wird fuer jede neue Position ausdruecklich
 * entschieden — ohne Vorbelegung, und ohne Entscheidung kein Anlegen. Eine
 * stille Vorbelegung wuerde je nach scope Geld verschenken ('all' deckte die
 * Zusatzleistung ungefragt) oder doppelt fordern (der Bearbeiter glaubt an
 * eine Deckung, die es nicht gibt).
 *
 * Keine Entscheidung noetig fuer:
 *  - Projekte ohne Pauschalposition (die Frage stellt sich nicht),
 *  - neue Positionen, die selbst Pauschale sind (eine Pauschale wird nie
 *    gedeckt — auch die halb angelegte nicht, sie soll auffallen statt
 *    stillgelegt zu werden),
 *  - Ids, die gar nicht (mehr) in der Liste stehen (wieder entfernt).
 */
export const pendingFlatInclusionDecisions = (
    services: FlatCoverageServiceLike[] | null | undefined,
    newServiceIds: string[] | null | undefined,
    decisions: Partial<Record<string, FlatInclusionDecision>> | null | undefined,
): string[] => {
    const list = Array.isArray(services) ? services : [];
    if (!list.some(isFlatRateService)) return [];
    const byId = new Map(list.map((s) => [s.id, s]));
    return (Array.isArray(newServiceIds) ? newServiceIds : []).filter((id) => {
        const s = byId.get(id);
        if (!s || isFlatRateLike(s)) return false;
        const decision = decisions?.[id];
        return decision !== 'included' && decision !== 'excluded';
    });
};

/**
 * Die gedeckten Ids EINER Pauschalposition nach der Gewinner-Regel von
 * resolveFlatCoverage — die Grundlage jeder Umschreibung auf 'explicit'.
 */
const coveredIdsOf = (
    services: FlatCoverageServiceLike[],
    flatId: string,
): string[] => {
    const out: string[] = [];
    resolveFlatCoverage(services).forEach((winner, coveredId) => {
        if (winner === flatId) out.push(coveredId);
    });
    return out;
};

/**
 * Schreibt die getroffenen Entscheidungen in die Deckung der
 * Pauschalpositionen, so dass resolveFlatCoverage danach GENAU das
 * Entschiedene liefert. Gibt eine neue Liste zurueck; unveraenderte
 * Positionen behalten ihre Objektreferenz (dieselbe Konvention wie
 * stampFlatCoverage).
 *
 * Warum umschreiben statt Merkliste: FlatRateCoverage kennt bewusst keine
 * Ausschlussliste. Muss ein regelbasierter scope ('all', 'inspection') eine
 * Position ausnehmen oder aufnehmen, wird er auf 'explicit' MATERIALISIERT —
 * die bis dahin gedeckten Ids werden festgeschrieben, dann wird die eine Id
 * ergaenzt bzw. entfernt. Fuer den Bestand aendert sich dabei nichts, und
 * jede kuenftige neue Position bekommt ohnehin ihre eigene ausdrueckliche
 * Entscheidung (Betreiber-Entscheidung 3) — der Regel-scope hat ab da keinen
 * Vorbelegungs-Auftrag mehr, den die Materialisierung nehmen koennte.
 *
 * 'included' geht an die ERSTE Pauschale in LV-Reihenfolge, sofern nicht
 * ohnehin eine deckt — deterministisch, dieselbe Rangfolge wie die
 * Gewinner-Regel in resolveFlatCoverage. 'excluded' raeumt die Deckung bei
 * JEDER Pauschale ab, die die Position traefe — sonst uebernaehme nach der
 * ersten die naechste in der Reihe.
 */
export const applyFlatInclusionDecisions = <T extends FlatCoverageServiceLike>(
    services: T[],
    decisions: Partial<Record<string, FlatInclusionDecision>> | null | undefined,
): T[] => {
    const entries = Object.entries(decisions ?? {}).filter(
        ([, d]) => d === 'included' || d === 'excluded',
    ) as Array<[string, FlatInclusionDecision]>;
    if (entries.length === 0 || !Array.isArray(services)) return services;

    let current = services;
    const rewriteFlat = (flatId: string, nextIds: string[]) => {
        current = current.map((s) =>
            s.id === flatId
                ? { ...s, flatRate: { scope: 'explicit' as const, serviceIds: nextIds } }
                : s,
        );
    };

    for (const [serviceId, decision] of entries) {
        const target = current.find((s) => s.id === serviceId);
        // Wieder entfernte Ids und Pauschalen selbst: nichts zu entscheiden.
        if (!target || isFlatRateLike(target)) continue;

        if (decision === 'included') {
            if (resolveFlatCoverage(current).has(serviceId)) continue;
            const firstFlat = current.find(isFlatRateService);
            if (!firstFlat) continue;
            rewriteFlat(firstFlat.id, [...coveredIdsOf(current, firstFlat.id), serviceId]);
        } else {
            // Schleife statt Einzelschritt: nach dem Abraeumen der ersten
            // Pauschale kann die naechste in der Rangfolge uebernehmen.
            // Begrenzt durch die Zahl der Pauschalen — jede wird hoechstens
            // einmal umgeschrieben.
            for (let guard = current.filter(isFlatRateService).length; guard > 0; guard--) {
                const winner = resolveFlatCoverage(current).get(serviceId);
                if (!winner) break;
                rewriteFlat(
                    winner,
                    coveredIdsOf(current, winner).filter((id) => id !== serviceId),
                );
            }
        }
    }
    return current;
};
